import { useWizardStore } from '@/store/wizardStore'
import type { EnvironmentType } from '@/store/wizardStore'
import type { Lang } from '@/hooks/useLang'

interface Step2EnvironmentProps { lang: Lang }

const ENVIRONMENTS: {
  id: EnvironmentType
  icon: string
  fr: { title: string; desc: string }
  en: { title: string; desc: string }
}[] = [
  {
    id: 'indoor',
    icon: 'corporate_fare',
    fr: { title: 'Intérieur', desc: 'Bureaux, commerces, hôpitaux — surfaces lisses, conditions contrôlées.' },
    en: { title: 'Indoor', desc: 'Offices, retail, hospitals — smooth surfaces, controlled conditions.' },
  },
  {
    id: 'outdoor',
    icon: 'park',
    fr: { title: 'Extérieur', desc: 'Espaces publics, sites industriels — terrain variable, intempéries possibles.' },
    en: { title: 'Outdoor', desc: 'Public spaces, industrial sites — variable terrain, possible adverse weather.' },
  },
  {
    id: 'both',
    icon: 'swap_horiz',
    fr: { title: 'Mixte', desc: 'Transition fluide entre environnements intérieurs et extérieurs.' },
    en: { title: 'Mixed', desc: 'Smooth transitions between indoor and outdoor environments.' },
  },
  {
    id: 'hazardous',
    icon: 'warning',
    fr: { title: 'Milieu hostile', desc: 'Sites à risque, zones de confinement ou environnements chimiques.' },
    en: { title: 'Hazardous', desc: 'High-risk sites, containment zones or chemical environments.' },
  },
]

export default function Step2Environment({ lang }: Step2EnvironmentProps) {
  const { draft, update } = useWizardStore()
  const selected = draft.step2_environment

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
      {ENVIRONMENTS.map((env) => {
        const isSelected = selected === env.id
        return (
          <div
            key={env.id}
            onClick={() => update({ step2_environment: env.id })}
            className={`glass-card p-8 rounded-xl border border-outline-variant/20 flex flex-col items-center text-center group cursor-pointer ${isSelected ? 'ring-1 ring-primary-container bg-surface-container-highest/80' : ''}`}
          >
            <div className={`w-16 h-16 rounded-xl flex items-center justify-center mb-6 transition-colors ${isSelected ? 'bg-primary-container/20 text-primary-container' : 'bg-surface-container-highest text-primary'}`}>
              <span className="material-symbols-outlined text-4xl">{env.icon}</span>
            </div>
            <h3 className={`font-headline text-xl font-bold mb-3 transition-colors ${isSelected ? 'text-primary-container' : 'group-hover:text-primary'}`}>
              {env[lang].title}
            </h3>
            <p className="text-sm text-on-surface-variant leading-relaxed">
              {env[lang].desc}
            </p>
            {isSelected && (
              <div className="mt-6 flex items-center gap-1 text-xs font-bold tracking-widest uppercase text-primary-container">
                <span className="material-symbols-outlined text-sm">check_circle</span>
                {lang === 'fr' ? 'Sélectionné' : 'Selected'}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
